import { useEffect, useState, type FormEvent } from 'react';
import { useTransactions } from '@/hooks/useTransactions';
import { useCategories } from '@/hooks/useCategories';
import { useAuth } from '@/hooks/useAuth';
import { formatCurrency, formatDate, cn } from '@/lib/utils';
import { transactions as transactionsApi } from '@/lib/api';
import type {
  CreateTransactionData,
  TransactionFilters,
} from '@/lib/types';
import {
  Plus,
  Search,
  ArrowUpRight,
  ArrowDownRight,
  Trash2,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  Download,
  Repeat,
} from 'lucide-react';

export default function TransactionsPage() {
  const {
    transactions,
    loading,
    error,
    page,
    totalPages,
    total,
    fetchTransactions,
    createTransaction,
    deleteTransaction,
  } = useTransactions();

  const { categories, fetchCategories } = useCategories();
  const { user } = useAuth();
  const isPro = user?.plan === 'pro' || user?.plan === 'enterprise';

  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState<TransactionFilters>({
    page: 1,
    limit: 20,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'income' | 'expense'>(
    'all'
  );
  const [exporting, setExporting] = useState(false);

  // Form state
  const [formAmount, setFormAmount] = useState('');
  const [formType, setFormType] = useState<'income' | 'expense'>('expense');
  const [formCategory, setFormCategory] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formDate, setFormDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [formRecurring, setFormRecurring] = useState(false);
  const [formRecurrenceInterval, setFormRecurrenceInterval] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    const newFilters: TransactionFilters = {
      ...filters,
      search: searchQuery || undefined,
      type: typeFilter === 'all' ? undefined : typeFilter,
    };
    fetchTransactions(newFilters);
  }, [fetchTransactions, filters, searchQuery, typeFilter]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      const data: CreateTransactionData = {
        amount: parseFloat(formAmount),
        type: formType,
        categoryId: formCategory,
        description: formDescription,
        date: formDate,
        isRecurring: formRecurring,
        recurrenceInterval: formRecurring ? formRecurrenceInterval : undefined,
      };
      await createTransaction(data);
      setShowForm(false);
      resetForm();
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Failed to create transaction'
      );
    } finally {
      setFormLoading(false);
    }
  }

  function resetForm() {
    setFormAmount('');
    setFormType('expense');
    setFormCategory('');
    setFormDescription('');
    setFormDate(new Date().toISOString().split('T')[0]);
    setFormRecurring(false);
    setFormRecurrenceInterval('monthly');
    setFormError('');
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }
    await deleteTransaction(id);
  }

  async function handleExportCsv() {
    if (!isPro) return;
    setExporting(true);
    try {
      const blob = await transactionsApi.exportCsv({
        type: typeFilter === 'all' ? undefined : typeFilter,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'budgetboard-transactions.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // Export failed silently
    } finally {
      setExporting(false);
    }
  }

  function goToPage(newPage: number) {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }

  return (
    <div className="animate-fadeIn space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Transactions</h1>
          <p className="mt-1 text-gray-400">
            {total} transaction{total !== 1 ? 's' : ''} total
          </p>
        </div>
        <div className="flex gap-2">
          {isPro && (
            <button
              onClick={handleExportCsv}
              disabled={exporting}
              className="btn-secondary"
            >
              {exporting ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Download size={18} className="mr-2" />
              )}
              {!exporting && 'Export CSV'}
            </button>
          )}
          <button onClick={() => setShowForm(true)} className="btn-primary">
            <Plus size={18} className="mr-2" />
            Add Transaction
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
          />
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-base pl-10"
          />
        </div>
        <div className="flex gap-2">
          {(['all', 'income', 'expense'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setTypeFilter(type)}
              className={cn(
                'rounded-lg px-4 py-2.5 text-sm font-medium transition-colors',
                typeFilter === type
                  ? 'bg-brand-600 text-white'
                  : 'bg-surface-secondary text-gray-400 hover:bg-surface-hover hover:text-white'
              )}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Transactions List */}
      {error && (
        <div className="rounded-lg bg-accent-red/10 px-4 py-3 text-sm text-accent-red">
          {error}
        </div>
      )}

      {loading && !transactions.length ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
        </div>
      ) : transactions.length === 0 ? (
        <div className="card py-12 text-center">
          <p className="text-gray-500">No transactions found</p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary mt-4"
          >
            <Plus size={18} className="mr-2" />
            Add your first transaction
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {transactions.map((tx) => (
            <div
              key={tx.id}
              className="flex items-center justify-between rounded-lg border border-surface-border bg-surface-secondary p-4 transition-colors hover:bg-surface-hover"
            >
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    'rounded-lg p-2',
                    tx.type === 'income'
                      ? 'bg-accent-green/10'
                      : 'bg-accent-red/10'
                  )}
                >
                  {tx.type === 'income' ? (
                    <ArrowUpRight className="h-5 w-5 text-accent-green" />
                  ) : (
                    <ArrowDownRight className="h-5 w-5 text-accent-red" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-white">{tx.description}</p>
                    {tx.isRecurring && (
                      <span className="flex items-center gap-1 rounded-full bg-brand-500/10 px-2 py-0.5 text-xs text-brand-400">
                        <Repeat size={10} />
                        {tx.recurrenceInterval}
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                    <span>{formatDate(tx.date)}</span>
                    {(tx.categoryName || tx.category_name) && (
                      <>
                        <span>-</span>
                        <span
                          className="rounded-full px-2 py-0.5"
                          style={{
                            backgroundColor: (tx.categoryColor || tx.category_color)
                              ? `${tx.categoryColor || tx.category_color}20`
                              : '#2a2a3a',
                            color: (tx.categoryColor || tx.category_color) || '#9ca3af',
                          }}
                        >
                          {tx.categoryName || tx.category_name}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <p
                  className={cn(
                    'text-sm font-semibold',
                    tx.type === 'income'
                      ? 'text-accent-green'
                      : 'text-accent-red'
                  )}
                >
                  {tx.type === 'income' ? '+' : '-'}
                  {formatCurrency(tx.amount)}
                </p>
                <button
                  onClick={() => handleDelete(tx.id)}
                  className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-accent-red/10 hover:text-accent-red"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1}
              className="btn-secondary"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => goToPage(page + 1)}
              disabled={page >= totalPages}
              className="btn-secondary"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Create Transaction Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="card w-full max-w-md animate-slideUp">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                Add Transaction
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="rounded-lg p-1 text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {formError && (
                <div className="rounded-lg bg-accent-red/10 px-4 py-3 text-sm text-accent-red">
                  {formError}
                </div>
              )}

              <div className="flex gap-2">
                {(['expense', 'income'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setFormType(type)}
                    className={cn(
                      'flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors',
                      formType === type
                        ? type === 'income'
                          ? 'bg-accent-green/20 text-accent-green'
                          : 'bg-accent-red/20 text-accent-red'
                        : 'bg-surface-tertiary text-gray-400 hover:text-white'
                    )}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">
                  Amount
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formAmount}
                  onChange={(e) => setFormAmount(e.target.value)}
                  placeholder="0.00"
                  required
                  className="input-base"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">
                  Description
                </label>
                <input
                  type="text"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="What was this for?"
                  required
                  className="input-base"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">
                  Category
                </label>
                <select
                  value={formCategory}
                  onChange={(e) => setFormCategory(e.target.value)}
                  required
                  className="input-base"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">
                  Date
                </label>
                <input
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  required
                  className="input-base"
                />
              </div>

              {/* Recurring Transaction (Pro) */}
              {isPro && (
                <div className="space-y-3 rounded-lg border border-surface-border bg-surface-tertiary p-4">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={formRecurring}
                      onChange={(e) => setFormRecurring(e.target.checked)}
                      className="h-4 w-4 rounded border-surface-border bg-surface-secondary text-brand-500 focus:ring-brand-500"
                    />
                    <span className="text-sm font-medium text-gray-300">
                      Recurring transaction
                    </span>
                    <span className="rounded-full bg-brand-500/10 px-2 py-0.5 text-xs text-brand-400">
                      Pro
                    </span>
                  </label>
                  {formRecurring && (
                    <select
                      value={formRecurrenceInterval}
                      onChange={(e) => setFormRecurrenceInterval(e.target.value as 'weekly' | 'monthly' | 'yearly')}
                      className="input-base"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="btn-primary flex-1"
                >
                  {formLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    'Add Transaction'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
